import type { CurrentUser, UserRole } from "./currentUserTypes";
import { can, type Capability } from "./permissions";

export interface AccessRule {
  capabilities: readonly Capability[];
  legacyRoles: readonly UserRole[];
}

const ALL_LEGACY_ROLES: readonly UserRole[] = [
  "super",
  "client",
  "client_store",
  "checker",
  "overviewer",
  "supplier",
];

export const ACCESS_RULES = {
  dashboard: {
    capabilities: ["dashboard.operations"],
    legacyRoles: ALL_LEGACY_ROLES,
  },
  supplierList: {
    capabilities: [],
    legacyRoles: ["supplier"],
  },
  quotation: {
    capabilities: ["quotation.manage"],
    legacyRoles: ["super", "client", "client_store"],
  },
  orders: {
    capabilities: ["order.manage"],
    legacyRoles: ["super", "client", "client_store", "overviewer", "supplier"],
  },
  checkin: {
    capabilities: ["checkin.manage"],
    legacyRoles: ALL_LEGACY_ROLES,
  },
  catalogProducts: {
    capabilities: ["catalog.manage"],
    legacyRoles: ["super", "overviewer", "supplier"],
  },
  catalogSuppliers: {
    capabilities: ["catalog.manage"],
    legacyRoles: ["super", "overviewer"],
  },
  catalogClients: {
    capabilities: ["catalog.manage"],
    legacyRoles: ["super", "overviewer"],
  },
  catalogStores: {
    capabilities: ["catalog.manage"],
    legacyRoles: ["super", "overviewer", "client", "client_store"],
  },
  alerts: {
    capabilities: ["alerts.manage"],
    legacyRoles: ["super", "supplier"],
  },
  users: {
    capabilities: ["users.manage"],
    legacyRoles: ["super", "client"],
  },
  reports: {
    capabilities: [],
    legacyRoles: ["super"],
  },
  more: {
    capabilities: ["catalog.manage"],
    legacyRoles: ALL_LEGACY_ROLES,
  },
} as const satisfies Record<string, AccessRule>;

// This projects server-granted capability IDs for frontend visibility and
// routing only. The API remains the authorization authority.
export function canAccess(
  user: CurrentUser | null | undefined,
  rule: AccessRule,
): boolean {
  if (!user) return false;

  return (
    rule.legacyRoles.includes(user.role) ||
    rule.capabilities.some((capability) => can(user, capability))
  );
}
